// src/ia/permisosIA.js
// ═══════════════════════════════════════════════════════════════
// Bloque 7D — Renty espeja la interfaz.
//
// Si un empleado no puede ver un módulo, las herramientas de ese módulo
// NO se le envían a Groq: el modelo ni siquiera sabe que existen, así que
// no hay nada que negar ni que negociar. Es más robusto que instruirle en
// el prompt "no respondas esto" — eso siempre se puede rodear hablando.
//
// ⚠️ Si agregas una herramienta nueva a aiSchema.js y pertenece a un módulo
//    bloqueable, agrégala TAMBIÉN a este mapa. Lo que no aparezca aquí queda
//    disponible para todos: es a propósito, para que una herramienta nueva
//    nunca le rompa Renty a nadie por olvido.
// ═══════════════════════════════════════════════════════════════

import { MODULOS, getModulosBloqueados } from "../config/modulos";

/**
 * Herramienta → módulos de los que depende.
 * Si CUALQUIERA de esos módulos está bloqueado, la herramienta desaparece.
 * Los ids son los mismos de src/config/modulos.js.
 */
export const HERRAMIENTAS_POR_MODULO = {
  // 📅 Agenda
  consultar_agenda: ["agenda"],
  consultar_agenda_fecha: ["agenda"],
  crear_cita: ["agenda"],
  consultar_citas: ["agenda"],
  cambiar_estado_cita: ["agenda"],
  consultar_notificaciones: ["agenda"],
  crear_nota: ["agenda"],

  // 🔍 Buscar documento
  consultar_pedidos: ["buscar-doc"],
  consultar_cotizaciones: ["buscar-doc"],

  // 💰 Contabilidad
  resumen_financiero: ["contabilidad"],
  comparativa_financiera: ["contabilidad"],

  // 📊 Dashboard
  articulos_mas_alquilados: ["reportes"],
  clientes_mas_frecuentes: ["reportes"],

  // Mixta: mezcla cotizaciones sin convertir con pedidos sin cobrar,
  // así que cae si cualquiera de los dos módulos está bloqueado.
  consultar_alertas_crm: ["contabilidad", "buscar-doc"],
};

/**
 * ¿Este usuario puede usar esta herramienta?
 * @param {string} nombre - nombre de la herramienta
 * @param {object} ctx - { esAdmin, perfil }
 */
export function puedeUsarHerramienta(nombre, ctx = {}) {
  const { esAdmin, perfil } = ctx;

  // Admin y super_admin nunca se autobloquean (misma regla que puedeVerModulo)
  if (esAdmin) return true;

  const modulos = HERRAMIENTAS_POR_MODULO[nombre];
  if (!modulos) return true; // sin mapa = módulo núcleo = disponible

  const bloqueados = getModulosBloqueados(perfil);
  if (bloqueados.length === 0) return true;

  return !modulos.some((m) => bloqueados.includes(m));
}

/**
 * Texto que se le agrega al prompt cuando el usuario tiene módulos bloqueados.
 *
 * Al quitarle herramientas, Llama tiende a agarrar otra parecida y responder
 * igual (nos pasó: le preguntaron por ingresos, no tenía la financiera, y
 * respondió con el conteo total de pedidos como si fuera del mes). Decirle
 * explícitamente qué NO puede responder evita esa sustitución.
 *
 * Reutiliza el campo `aviso` de MODULOS para no duplicar textos.
 */
export function avisoModulosBloqueados(ctx = {}) {
  const { esAdmin, perfil } = ctx;
  if (esAdmin) return "";

  const bloqueados = getModulosBloqueados(perfil);
  if (!bloqueados.length) return "";

  // Solo avisamos de módulos que tienen herramientas: si le bloqueas Recepción,
  // Renty no cambia en nada y no vale la pena gastar tokens.
  const conHerramientas = new Set(Object.values(HERRAMIENTAS_POR_MODULO).flat());
  const relevantes = bloqueados.filter((id) => conHerramientas.has(id));
  if (!relevantes.length) return "";

  const lineas = relevantes
    .map((id) => {
      const mod = MODULOS.find((m) => m.id === id);
      return mod ? `- ${mod.titulo}: ${mod.aviso || "sin acceso"}` : null;
    })
    .filter(Boolean);

  return `

ACCESO LIMITADO DE ESTE USUARIO (CRÍTICO):
Este usuario NO tiene acceso a:
${lineas.join("\n")}
Si pregunta algo de esos temas, dile con naturalidad que no tienes acceso a esa información con su usuario y que se la pida al administrador. NUNCA uses otra herramienta como sustituto para responder igual.`;
}

/** Devuelve solo las herramientas que este usuario puede usar. */
export function filtrarHerramientas(tools = [], ctx = {}) {
  const { esAdmin, perfil } = ctx;
  if (esAdmin) return tools;

  const bloqueados = getModulosBloqueados(perfil);
  if (bloqueados.length === 0) return tools;

  return tools.filter((t) => puedeUsarHerramienta(t?.function?.name, ctx));
}